import { useState, useMemo, useEffect } from 'react';
import { IonContent, IonPage, IonRefresher, IonRefresherContent } from '@ionic/react';
import type { RefresherEventDetail } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Logo } from '@/components/ui/Logo';
import { UserMenu } from '@/components/shell/UserMenu';
import { getPublishedDocuments } from '@/lib/api/documents';
import type { PublishedDocumentSummary } from '@/lib/api/documents';
import { supabase } from '@/lib/supabase/client';


type NavTab = 'Latest' | 'Most read' | 'A-Z' | 'By author' | 'All publications';
const NAV_ITEMS: NavTab[] = ['Latest', 'Most read', 'A-Z', 'By author', 'All publications'];

function formatDate(iso: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function DocImage({ url, aspectClass }: { url: string | null; aspectClass: string }) {
  if (url) {
    return (
      <div className={`relative w-full overflow-hidden rounded-sm ${aspectClass}`}>
        <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover object-top" loading="lazy" />
      </div>
    );
  }
  return (
    <div className={`relative w-full overflow-hidden rounded-sm ${aspectClass}`}>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ background: 'linear-gradient(140deg, #0f1e2b 0%, #1e3a4a 55%, #0a2035 100%)' }}
      >
        <svg width="75%" height="70%" viewBox="0 0 400 200" style={{ opacity: 0.35 }}>
          {[0, 40, 80, 120, 160, 200, 240, 280, 320, 360, 400].map(x => (
            <line key={x} x1={x} y1="0" x2={x} y2="200" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
          ))}
          {[0, 40, 80, 120, 160, 200].map(y => (
            <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
          ))}
          <polyline points="0,175 60,168 110,155 175,130 240,95 310,70 400,55" fill="none" stroke="#1a56a0" strokeWidth="2" />
          {[{ x: 60, y: 168 }, { x: 175, y: 130 }, { x: 310, y: 70 }].map(p => (
            <circle key={p.x} cx={p.x} cy={p.y} r="4" fill="#1a56a0" />
          ))}
        </svg>
      </div>
    </div>
  );
}

function HeroCard({ doc, onClick }: { doc: PublishedDocumentSummary; onClick: () => void }) {
  return (
    <div className="group flex flex-col gap-2.5 cursor-pointer" onClick={onClick}>
      <DocImage url={doc.coverImageUrl} aspectClass="aspect-video" />
      <div className="flex flex-col gap-1.5 pt-1">
        <span className="text-[0.67rem] font-extrabold uppercase tracking-widest text-[#1a56a0] group-hover:underline">
          {doc.publication.title}
        </span>
        <h1
          className="font-serif text-[1.8rem] font-bold leading-tight transition-colors group-hover:text-[#1a56a0]"
          style={{ textWrap: 'pretty' } as React.CSSProperties}
        >
          {doc.title}
        </h1>
        <p className="text-sm text-gray-500">
          By {doc.publication.authorName}
          {doc.publishedAt && <> &nbsp;·&nbsp; {formatDate(doc.publishedAt)}</>}
          {doc.viewCount > 0 && <> &nbsp;·&nbsp; {doc.viewCount.toLocaleString()} views</>}
        </p>
      </div>
    </div>
  );
}

function RailCard({ doc, onClick, isLast }: { doc: PublishedDocumentSummary; onClick: () => void; isLast: boolean }) {
  return (
    <>
      <div className="group flex cursor-pointer gap-3" onClick={onClick}>
        {doc.coverImageUrl && (
          <img
            src={doc.coverImageUrl}
            alt=""
            className="mt-0.5 h-24 w-16 shrink-0 rounded-sm object-cover object-center"
            loading="lazy"
          />
        )}
        <div className="min-w-0">
          <span className="text-[0.67rem] font-extrabold uppercase tracking-widest text-[#1a56a0]">
            {doc.publication.title}
          </span>
          <div className="font-serif text-[0.95rem] font-bold leading-snug transition-colors group-hover:text-[#1a56a0]">
            {doc.title}
          </div>
          <div className="text-[0.8rem] text-gray-500">
            By {doc.publication.authorName}
            {doc.publishedAt && <> &nbsp;·&nbsp; {formatDate(doc.publishedAt)}</>}
            {doc.viewCount > 0 && <> &nbsp;·&nbsp; {doc.viewCount.toLocaleString()}</>}
          </div>
        </div>
      </div>
      {!isLast && <div className="my-2.5 h-px bg-gray-200" />}
    </>
  );
}

function GridCard({ doc, onClick }: { doc: PublishedDocumentSummary; onClick: () => void }) {
  return (
    <div className="group cursor-pointer border-t border-gray-100 pt-3 pb-3" onClick={onClick}>
      <div className="mb-2">
        <DocImage url={doc.coverImageUrl} aspectClass="aspect-[2/3]" />
      </div>
      <span className="text-[0.62rem] font-extrabold uppercase tracking-widest text-[#1a56a0]">
        {doc.publication.title}
      </span>
      <div
        className="mt-0.5 font-serif text-[0.95rem] font-bold leading-snug transition-colors group-hover:text-[#1a56a0]"
        style={{ textWrap: 'pretty' } as React.CSSProperties}
      >
        {doc.title}
      </div>
      <div className="mt-0.5 text-[0.78rem] text-gray-500">
        {doc.publication.authorName}
        {doc.viewCount > 0 && <> &nbsp;·&nbsp; {doc.viewCount.toLocaleString()} views</>}
      </div>
    </div>
  );
}

export default function CoverPage() {
  const [activeNav, setActiveNav] = useState<NavTab>('Latest');
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data: allDocs = [], isLoading } = useQuery({
    queryKey: ['published-documents', 500],
    queryFn: () => getPublishedDocuments(500),
    // Refetch every 5 minutes in the background so view counts and new articles
    // stay fresh without the user needing to reload the page.
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });

  // Realtime: invalidate immediately when any document is published or updated.
  useEffect(() => {
    const channel = supabase
      .channel('cover-documents')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'documents' }, () => {
        queryClient.invalidateQueries({ queryKey: ['published-documents'] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  async function handleRefresh(e: CustomEvent<RefresherEventDetail>) {
    await queryClient.refetchQueries({ queryKey: ['published-documents', 500] });
    e.detail.complete();
  }

  const docs = useMemo(() => {
    const sorted = [...allDocs];
    if (activeNav === 'Most read') sorted.sort((a, b) => b.viewCount - a.viewCount);
    if (activeNav === 'A-Z') sorted.sort((a, b) => a.title.localeCompare(b.title));
    if (activeNav === 'By author') sorted.sort((a, b) => a.publication.authorName.localeCompare(b.publication.authorName));
    // 'Latest' keeps the default DB order (published_at desc)
    return sorted;
  }, [allDocs, activeNav]);

  const hero = docs[0];
  const rail = docs.slice(1, 5);
  const grid = docs.slice(5);

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        {/* pullMin=80 requires a deliberate pull; no pullFactor override so the native
            platform timing fires onIonRefresh only on release (not mid-drag). */}
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh} pullMin={80}>
          <IonRefresherContent
            pullingIcon="arrow-down-circle-outline"
            pullingText="Pull for fresh reads 📚"
            refreshingSpinner="crescent"
            refreshingText="Scanning the stacks…"
          />
        </IonRefresher>

        {/* Sticky header */}
        <header className="sticky top-0 z-50 bg-white">
          <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-2">
            <div className="flex shrink-0 items-center gap-3">
              <Logo size="sm" />
              <span className="hidden sm:block font-serif text-[0.72rem] italic text-gray-400">
                Freedom to read &amp; publish
              </span>
            </div>

            <div className="hidden sm:flex flex-1 max-w-xs items-center gap-2 rounded-full bg-gray-100 px-3.5 py-1.5 mx-4">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                placeholder="Search publications"
                className="w-full border-none bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
                aria-label="Search publications"
              />
            </div>

            <div className="flex items-center gap-1.5">
              <button
                className="flex sm:hidden h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100"
                aria-label="Search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </button>
              <UserMenu />
            </div>
          </div>

          <nav className="flex justify-center overflow-x-auto border-b border-gray-200">
            {NAV_ITEMS.map(item => (
              <button
                key={item}
                onClick={() => {
                  if (item === 'All publications') { history.push('/publications'); return; }
                  setActiveNav(item);
                }}
                className={`shrink-0 whitespace-nowrap border-b-2 px-5 py-2.5 text-[0.86rem] font-medium transition-colors ${
                  activeNav === item && item !== 'All publications'
                    ? 'border-gray-900 font-semibold text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-900'
                }`}
              >
                {item}
              </button>
            ))}
          </nav>
        </header>

        <main className="mx-auto max-w-5xl px-6 py-6 pb-16">
          {isLoading && (
            <div className="flex justify-center py-24">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
            </div>
          )}

          {!isLoading && docs.length === 0 && (
            <div className="flex flex-col items-center gap-4 py-24 text-center">
              <p className="text-gray-500">No published content yet.</p>
              <button
                onClick={() => history.push('/publications/new')}
                className="rounded-lg bg-[#2f3e46] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33]"
              >
                Create a publication
              </button>
            </div>
          )}

          {!isLoading && hero && (
            <>
              {/* Hero + short reads rail */}
              <div className="grid grid-cols-1 gap-8 md:grid-cols-[1fr_270px]">
                <HeroCard doc={hero} onClick={() => history.push(`/read/${hero.id}`)} />

                {rail.length > 0 && (
                  <div className="border-l border-gray-200 pl-6">
                    <p className="mb-3.5 text-[0.62rem] font-extrabold uppercase tracking-widest text-gray-400">
                      FEATURED
                    </p>
                    {rail.map((doc, i) => (
                      <RailCard
                        key={doc.id}
                        doc={doc}
                        onClick={() => history.push(`/read/${doc.id}`)}
                        isLast={i === rail.length - 1}
                      />
                    ))}
                  </div>
                )}
              </div>

              {grid.length > 0 && (
                <>
                  <div className="my-6 h-px bg-gray-200" />
                  <p className="mb-4 text-[0.62rem] font-extrabold uppercase tracking-widest text-gray-400">
                    More from Open iPub
                  </p>
                  <div className="grid grid-cols-1 gap-x-6 gap-y-0 sm:grid-cols-2 lg:grid-cols-4">
                    {grid.map(doc => (
                      <GridCard
                        key={doc.id}
                        doc={doc}
                        onClick={() => history.push(`/read/${doc.id}`)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </IonContent>
    </IonPage>
  );
}
